import React from "react";
import {
  BlockStack,
  Card,
  FormLayout,
  Layout,
  TextField,
  Text,
  SkeletonDisplayText,
  SkeletonBodyText,
  Checkbox,
  Banner,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { CountryPicker } from "@shared/ui";
import { LanguagePicker } from "@shared/ui";
import { useHealthcareCompanyMeta } from "@shared/hooks/common/useHealthcareCompanyMeta";
import useFormattedDate from "@shared/hooks/common/useFormattedDate";
import TaxId from "./TaxNumberComponent";
import ReactMarkdown from "react-markdown";

interface CompanyBillingDetailsProps {
  verifiedBusinessAt?: string;
  businessName: string;
  taxNumber: string;
  taxType: string;
  address: string;
  apartment: string;
  city: string;
  provinceCode: string;
  country: string;
  sendInvoices: boolean;
  billingEmails: string;
  invoiceLanguageCode: string;
  isLoading: boolean;
  businessMeta?: Record<string, unknown> | null;
  errors: Record<string, boolean>;
  apiErrors: Record<string, string>;
  handleInputChange: (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => void;
  handleInputChangeWithApiErrorClear: (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    fieldName: string
  ) => void;
  handleBooleanChange: (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    value: boolean
  ) => void;
  setBusinessName: React.Dispatch<React.SetStateAction<string>>;
  setTaxNumber: React.Dispatch<React.SetStateAction<string>>;
  setTaxType: React.Dispatch<React.SetStateAction<string>>;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  setApartment: React.Dispatch<React.SetStateAction<string>>;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setProvinceCode: React.Dispatch<React.SetStateAction<string>>;
  setCountry: React.Dispatch<React.SetStateAction<string>>;
  setSendInvoices: React.Dispatch<React.SetStateAction<boolean>>;
  setBillingEmails: React.Dispatch<React.SetStateAction<string>>;
  setInvoiceLanguageCode: React.Dispatch<React.SetStateAction<string>>;
}

const CompanyBillingDetails: React.FC<CompanyBillingDetailsProps> = ({
  verifiedBusinessAt,
  businessName,
  businessMeta,
  taxNumber,
  taxType,
  address,
  apartment,
  city,
  provinceCode,
  country,
  sendInvoices,
  billingEmails,
  invoiceLanguageCode,
  isLoading,
  errors,
  apiErrors,
  handleInputChange,
  handleInputChangeWithApiErrorClear,
  handleBooleanChange,
  setBusinessName,
  setTaxNumber,
  setTaxType,
  setAddress,
  setApartment,
  setCity,
  setProvinceCode,
  setCountry,
  setSendInvoices,
  setBillingEmails,
  setInvoiceLanguageCode,
}) => {
  const { t } = useTranslation();
  const { invoiceLanguages } = useHealthcareCompanyMeta();
  
  // Check if business is verified
  const isBusinessVerified = !!verifiedBusinessAt;
  
  // Format the verification date if available
  const { formattedDate: formattedVerificationDate } = useFormattedDate({ 
    date: verifiedBusinessAt || "" 
  });

  const renderSkeleton = () => {
    return (
      <BlockStack gap={"400"}>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
        <BlockStack gap={"200"}>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </BlockStack>
    );
  };

  return (
    <Layout.Section>
      <Card>
        <BlockStack gap="600">
          {/* Billing Information Section */}
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h1" variant="headingMd">
                {t("companyDetails.billingInformation")}
              </Text>

              <Text as="p" tone="subdued">
                {t("companyDetails.billingInformationDescription")}
              </Text>
            </BlockStack>
            {isLoading ? (
              renderSkeleton()
            ) : (
              <>
                {/* Business Verification Banner */}
                {isBusinessVerified && (
                  <Banner
                    title={t("companyDetails.businessVerified.title")}
                    tone="info"
                  >
                    <ReactMarkdown components={{
                      p: ({ children }) => (
                        <Text as="p">
                          {children}
                        </Text>
                      ),
                    }}>
                      {t("companyDetails.businessVerified.description", { 
                        date: `**${formattedVerificationDate}**` 
                      })}
                    </ReactMarkdown>
                  </Banner>
                )}

                <FormLayout>
                <TaxId
                  taxNumber={taxNumber}
                  taxType={taxType}
                  errors={errors}
                  handleInputChange={handleInputChange}
                  setTaxNumber={setTaxNumber}
                  setTaxType={setTaxType}
                  disabled={isBusinessVerified}
                />

                <TextField
                    label={t("companyDetails.legalBusinessName")}
                    value={businessName}
                    onChange={(value) =>
                    handleInputChange(setBusinessName, value)
                    }
                    autoComplete="off"
                    requiredIndicator
                    disabled={isBusinessVerified}
                    error={
                    errors.businessName &&
                    t("common.requiredField", {
                        field_name: t("companyDetails.legalBusinessName"),
                    })
                    }
                />

                <TextField
                  label={t("companyDetails.address")}
                  value={address}
                  onChange={(value) => handleInputChange(setAddress, value)}
                  autoComplete="off"
                  requiredIndicator
                  disabled={isBusinessVerified}
                  error={
                    errors.address &&
                    t("common.requiredField", {
                      field_name: t("companyDetails.address"),
                    })
                  }
                />

                <TextField
                  label={t("companyDetails.apartment")}
                  value={apartment}
                  onChange={(value) => handleInputChange(setApartment, value)}
                  autoComplete="off"
                  disabled={isBusinessVerified}
                />

                <FormLayout.Group>
                  <TextField
                    label={t("companyDetails.city")}
                    value={city}
                    onChange={(value) => handleInputChange(setCity, value)}
                    autoComplete="off"
                    requiredIndicator
                    disabled={isBusinessVerified}
                    error={
                      errors.city &&
                      t("common.requiredField", {
                        field_name: t("companyDetails.city"),
                      })
                    }
                  />
                  <TextField
                    label={t("companyDetails.postalCode")}
                    value={provinceCode}
                    onChange={(value) => handleInputChange(setProvinceCode, value)}
                    autoComplete="off"
                    disabled={isBusinessVerified}
                  />
                </FormLayout.Group>

                <CountryPicker
                  label={t("companyDetails.country")}
                  value={country || null}
                  onChange={(value) => handleInputChange(setCountry, value || "")}
                  required
                  disabled={isBusinessVerified}
                  error={
                    errors.country &&
                    t("common.requiredField", {
                      field_name: t("companyDetails.country"),
                    })
                  }
                />
              </FormLayout>
              </>
            )}
          </BlockStack>

          {/* Invoice Settings Section */}
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h1" variant="headingMd">
                {t("companyDetails.InvoiceConfiguration.title")}
              </Text>
            </BlockStack>
            {isLoading ? (
              renderSkeleton()
            ) : (
              <FormLayout>
                <LanguagePicker
                  label={t("companyDetails.invoiceLanguage")}
                  languageCode={invoiceLanguageCode}
                  onChange={(value) => handleInputChange(setInvoiceLanguageCode, value || "")}
                  filteredLanguageCodeList={invoiceLanguages}
                  required={true}
                />

                <Checkbox
                  label={t("companyDetails.sendInvoices")}
                  checked={sendInvoices}
                  onChange={(checked) => handleBooleanChange(setSendInvoices, checked)}
                />

                {sendInvoices && (
                  <TextField
                    label={t("companyDetails.billingEmail")}
                    value={billingEmails}
                    onChange={(value) => handleInputChangeWithApiErrorClear(setBillingEmails, value, "billingEmails")}
                    autoComplete="email"
                    type="email"
                    helpText={t("companyDetails.billingEmailHelp")}
                    error={apiErrors.billingEmails}
                  />
                )}
              </FormLayout>
            )}
          </BlockStack>

          {/* Meta Information Section - Only show if there's data */}
          {businessMeta && (
            <BlockStack gap="400">
                <BlockStack gap="200">
                <Text as="h1" variant="headingMd">
                    {t("companyDetails.AdditionalInformation.title")}
                </Text>
                </BlockStack>
                {isLoading ? (
                renderSkeleton()
                ) : (
                <FormLayout>
                </FormLayout>
                )}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Layout.Section>
  );
};

export default CompanyBillingDetails;